import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { AuthContext } from "./AuthContext";
import { SessionContext } from "./SessionContext";
import { OrganizationContextRecovery } from "./OrganizationContextRecovery";
import { ShellProvider } from "@/features/shell/ShellProvider";
import { ThemeProvider } from "@/features/shell/ThemeProvider";
import { LanguagePreferenceDialog } from "@/components/i18n/LanguagePreferenceDialog";
import api, {
  clearAccessToken,
  getAccessToken,
  refreshAccessToken,
  setAccessToken,
} from "@/lib/api";
import type { User, TokenPair } from "@/lib/types";
import type { BootstrapResponse } from "@/lib/bootstrap-types";
import type { ShellAccess } from "@/features/shell/types";
import { useI18n } from "@/lib/i18n";
import { isPlatformOwnerBootstrapContext } from "./bootstrapContext";
import { hasManualLogout, redirectToKeycloakLogout } from "./oidc";

interface AuthProviderProps {
  children: ReactNode;
}

/** Map BootstrapUser to the legacy User type for backward compatibility. */
function toUser(bootstrap: BootstrapResponse): User {
  return {
    id: bootstrap.user.id,
    email: bootstrap.user.email,
    username: bootstrap.user.username,
    first_name: bootstrap.user.first_name,
    last_name: bootstrap.user.last_name,
    is_active: true,
  };
}

const DEFAULT_SHELL_ACCESS: ShellAccess = {
  global: false,
  ops: false,
  client: false,
  field: false,
};

export function AuthProvider({ children }: AuthProviderProps) {
  const { syncLanguagePreference } = useI18n();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<BootstrapResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionLoading, setIsSessionLoading] = useState(true);

  const fetchSession = useCallback(async () => {
    if (import.meta.env.VITE_OIDC_ENABLED === "true" && hasManualLogout()) {
      clearAccessToken();
      setUser(null);
      setSession(null);
      setIsLoading(false);
      setIsSessionLoading(false);
      return;
    }
    let token = getAccessToken();
    if (!token && import.meta.env.VITE_OIDC_ENABLED === "true") {
      try {
        token = await refreshAccessToken();
      } catch {
        clearAccessToken();
      }
    }
    if (!token) {
      setIsLoading(false);
      setIsSessionLoading(false);
      return;
    }
    try {
      const { data: envelope } = await api.get<
        { data: BootstrapResponse } | BootstrapResponse
      >("/accounts/session/bootstrap/");
      // Handle both envelope {status, data, meta} and raw responses
      const bootstrapData =
        "data" in envelope && "status" in (envelope as Record<string, unknown>)
          ? (envelope as { data: BootstrapResponse }).data
          : (envelope as BootstrapResponse);

      setSession(bootstrapData);
      setUser(toUser(bootstrapData));
      syncLanguagePreference(
        bootstrapData.user.locale,
        bootstrapData.user.locale_confirmed,
      );

      const platformOwnerContext =
        isPlatformOwnerBootstrapContext(bootstrapData);

      // Persist only real client contexts for the axios interceptor. For a
      // platform admin, a platform_owner bootstrap is the backend anchor for
      // "All clients"; storing it would collapse the next request back to the
      // technical owner instead of the aggregate client scope.
      if (!platformOwnerContext && bootstrapData.active_organization?.id) {
        localStorage.setItem(
          "active_organization_id",
          bootstrapData.active_organization.id,
        );
      } else if (platformOwnerContext) {
        localStorage.removeItem("active_organization_id");
      }
      if (!platformOwnerContext && bootstrapData.active_workspace?.id) {
        localStorage.setItem(
          "active_workspace_id",
          bootstrapData.active_workspace.id,
        );
      } else if (platformOwnerContext) {
        localStorage.removeItem("active_workspace_id");
      }
    } catch {
      // Bootstrap might fail (e.g. no membership) — try fallback to /auth/me/
      try {
        const { data: meEnvelope } = await api.get<{ data: User } | User>(
          "/auth/me/",
        );
        const userData =
          "data" in meEnvelope &&
          "status" in (meEnvelope as Record<string, unknown>)
            ? (meEnvelope as { data: User }).data
            : (meEnvelope as User);
        setUser(userData);
        setSession(null);
      } catch {
        clearAccessToken();
        setUser(null);
        setSession(null);
      }
    } finally {
      setIsLoading(false);
      setIsSessionLoading(false);
    }
  }, [syncLanguagePreference]);

  useEffect(() => {
    void fetchSession();
  }, [fetchSession]);

  const login = useCallback(
    async (username: string, password: string) => {
      const { data: envelope } = await api.post<
        { data: TokenPair } | TokenPair
      >("/auth/token/", {
        username,
        password,
      });
      const tokens =
        "data" in envelope && "status" in (envelope as Record<string, unknown>)
          ? (envelope as { data: TokenPair }).data
          : (envelope as TokenPair);
      setAccessToken(tokens.access);
      if (import.meta.env.VITE_OIDC_ENABLED !== "true" && tokens.refresh) {
        localStorage.setItem("refresh_token", tokens.refresh);
      }
      await fetchSession();
    },
    [fetchSession],
  );

  const logout = useCallback(async () => {
    if (import.meta.env.VITE_OIDC_ENABLED === "true") {
      try {
        await api.post("/auth/logout/");
      } catch {
        // Continue to the corporate logout boundary. The manual-logout marker
        // prevents the refresh cookie from silently restoring this session.
      }
    }
    clearAccessToken();
    localStorage.removeItem("active_organization_id");
    localStorage.removeItem("active_workspace_id");
    setUser(null);
    setSession(null);
    if (!redirectToKeycloakLogout()) {
      window.location.href = "/login";
    }
  }, []);

  const refreshSession = useCallback(async () => {
    setIsSessionLoading(true);
    await fetchSession();
  }, [fetchSession]);

  const shellAccess = session?.shell_access ?? DEFAULT_SHELL_ACCESS;

  const authValue = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
    }),
    [user, isLoading, login, logout],
  );

  const sessionValue = useMemo(
    () => ({
      session,
      isSessionLoading,
      refreshSession,
    }),
    [session, isSessionLoading, refreshSession],
  );

  return (
    <AuthContext value={authValue}>
      <SessionContext value={sessionValue}>
        <OrganizationContextRecovery />
        <ShellProvider shellAccess={shellAccess}>
          <ThemeProvider>
            {children}
            <LanguagePreferenceDialog
              enabled={!!user && !isLoading && !isSessionLoading}
            />
          </ThemeProvider>
        </ShellProvider>
      </SessionContext>
    </AuthContext>
  );
}
